import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface FaqItem {
  q: string;
  a: string;
}

interface ProjectFAQProps {
  faq: FaqItem[];
}

export default function ProjectFAQ({ faq }: ProjectFAQProps) {
  const { ref, isVisible } = useScrollReveal();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (!faq || faq.length === 0) return null;

  return (
    <section ref={ref} className="w-full py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-5 md:px-10 lg:px-16">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-3">
            Common Questions
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-charcoal-300 leading-tight mb-4">
            Frequently Asked <span className="text-[#d4853a]">Questions</span>
          </h2>
          <p className="text-[15px] text-charcoal-50 max-w-xl mx-auto leading-relaxed">
            Everything you need to know before making your investment decision.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:gap-4">
          {faq.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`bg-[#FAFAF8] rounded-xl border transition-all duration-300 ${
                  isOpen ? 'border-[#d4853a]/20 shadow-[0_4px_20px_rgba(0,0,0,0.04)]' : 'border-ivory-200 hover:border-ivory-300'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: isVisible ? `${i * 80}ms` : '0ms' }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-start gap-4 p-5 md:p-6 text-left"
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 transition-all duration-300 ${
                    isOpen ? 'bg-[#d4853a] text-white' : 'bg-ivory-200 text-charcoal-100'
                  }`}>
                    <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <span className={`text-[14px] md:text-[15px] font-bold transition-colors duration-300 ${
                      isOpen ? 'text-[#d4853a]' : 'text-charcoal-300'
                    }`}>
                      {item.q}
                    </span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-charcoal-50 shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                <div className={`overflow-hidden transition-all duration-400 ease-out ${isOpen ? 'max-h-[400px]' : 'max-h-0'}`}>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 pl-[72px]">
                    <p className="text-[13px] md:text-[14px] text-charcoal-50 leading-[1.8]">{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}